import {addDoc, collection, doc, updateDoc} from "firebase/firestore";
import {Button, Col, Form, Modal, Row, Spinner, Table} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faSave} from "@fortawesome/free-solid-svg-icons";
import {FormEvent, useEffect, useState} from "react";
import {
    dateToString,
    removeEmptyFields,
    showErrorsListInToast,
    textToFloat,
    zeroToEmptyStringAsCurrency,
} from "../../tools";
import {RREmployeePPH, RREmployeePPHs, RREmployeeType} from "../../types/RREmployeeType";
import {appAuth, appFirestore} from "../../../index";
import ReactDatePicker from "react-datepicker";
import {MaybeDate} from "../../types/Maybe";
import {RRDEPARTMENT_MONTAGE} from "../../types/RRDepartmentType";
import {useAppSelector} from "../../hooks";
import {selectUsers} from "../../../features/AppAuthSlice";
import {useUpdateEmail, useUpdateProfile} from "react-firebase-hooks/auth";
import {selectCurrentUser, selectDepartment} from "../../../features/AppStateSlice";
import RightAlignedColumn from "../../components/RightAlignedColumn";
import CenteredColumn from "../../components/CenteredColumn";
import {AddIconButton} from "../../components/IconButtons";
import StringInputField from "../../components/inputs/StringInputField";
import {
    createEmployeeRate,
    getEmployeeRateActor,
    getEmployeeRateEffectiveFrom,
    prepareEmployeeRateEdit,
} from "./employeeRateAudit";
import {assertEmployeeEmailAvailable, normalizeEmployeeEmail} from "./employeeEmail";


export interface EmployeeDetailsProps {
    employee: RREmployeeType | null;
    onDone: () => any;
}

export function EmployeeDetails({
                                    employee,
                                    onDone
                                }: EmployeeDetailsProps) {
    const department = useAppSelector(selectDepartment);
    const [saving, setSaving] = useState(false);
    const [startDate, setStartDate] = useState<Date>(employee?.startDate || new Date());
    const [leaveDate, setLeaveDate] = useState<MaybeDate>(employee?.leaveDate);
    const users = useAppSelector(selectUsers);
    const currentAppUser = useAppSelector(selectCurrentUser);
    const [updateProfile] = useUpdateProfile(appAuth);
    const [updateEmail] = useUpdateEmail(appAuth);
    const [emplPPH, setEmplPPH] = useState<RREmployeePPHs>([...(employee?.empl_pph || [])]);

    useEffect(() => {
        setEmplPPH(employee?.empl_pph || []);
    }, [employee]);

    const save = async (data: any) => {
        const objectsRef = collection(appFirestore, "employees");
        data[`listIndex_${department}`] = null;
        data.email = data.email.toLowerCase().trim();
        let _data = removeEmptyFields(data);
        if (!_data.leaveDate) _data.leaveDate = null;

        if (employee?.id) {
            const user = users.find(
                u => u.employeeId === employee.id
            );

            if (user && user.name !== _data.name) {
                const success = await updateProfile({displayName: _data.name});
                if (!success) {
                    showErrorsListInToast("Грешка", ["Възникна грешка при опит да се запазят данните"]);
                    setSaving(false);
                    return
                }
            }

            if (user && user.email !== _data.email) {
                const success = await updateEmail(_data.email);
                if (!success) {
                    showErrorsListInToast("Грешка", ["Възникна грешка при опит да се запазят данните."]);
                    setSaving(false);
                    return
                }
            }

            setSaving(true);

            if (user) {
                const newUser = {
                    employeeId: employee.id,
                    egn: _data.egn,
                    name: _data.name,
                    phone: _data.phone || "",
                    email: _data.email!,
                    validFromDate: _data.startDate,
                    validToDate: _data.leaveDate,
                    validPeriod: {
                        from: _data.startDate,
                        to: _data.leaveDate
                    }
                }
                await updateDoc(user.ref!, newUser);
            }
            await updateDoc(doc(objectsRef, employee?.id), _data);
        } else {
            _data[`listIndex_${department}`] = 9999999;
            await addDoc(objectsRef, _data);
        }
        setSaving(false);
        onDone();
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (saving) return;
        const form = new FormData(event.currentTarget);
        let re = /\S+@\S+\.\S+/;

        let lastPPH: RREmployeePPH | undefined;
        for(let i=emplPPH.length-1; i>=0; i--) {
            if(emplPPH[i].isApproved && !lastPPH) {
                lastPPH = emplPPH[i];
            }
        }
        const data: RREmployeeType = {
            isSuperAdmin: employee?.isSuperAdmin || false,
            isAdmin: employee?.isAdmin || false,
            isEmployee: employee?.isEmployee || true,
            isCatering: employee?.isCatering || department === "catering",
            isMontage: employee?.isMontage || department === "montage",
            isStore: employee?.isStore || department === "store",
            isLogistics: employee?.isLogistics || department === "logistics",
            canSeePayment: employee?.canSeePayment || false,
            canSeeLogisticsScheduleReport: employee?.canSeeLogisticsScheduleReport || false,
            canUseMobileApp: employee?.canUseMobileApp || true,
            canUseWebApp: employee?.canUseWebApp || false,
            department: RRDEPARTMENT_MONTAGE, //event.target.elements.department.value,
            egn: String(form.get("egn") || ""),
            name: String(form.get("name") || ""),
            email: normalizeEmployeeEmail(String(form.get("email") || "")),
            phone: String(form.get("phone") || ""),
            empl_pph: [...emplPPH],
            pph: lastPPH?.pph || 0,
            supervisor_pph: lastPPH?.supervisor_pph || 0,
            // pph: textToFloat(event.target.elements.pph.value) || 0,
            // supervisor_pph: textToFloat(event.target.elements.supervisor_pph.value) || 0,
            isDeleted: false,
            startDate: startDate,
            leaveDate: leaveDate,
            listIndex_montage: employee?.listIndex_montage || 999999999,
            listIndex_catering: employee?.listIndex_catering || 999999999,
            listIndex_store: employee?.listIndex_store || 999999999,
            listIndex_logistics: employee?.listIndex_logistics || 999999999,
        };

        const errors: string[] = [];

        if (!data.email || data.email.length === 0 || !re.test(data.email)) {
            errors.push("Въведете валиден e-mail адрес.")
        }
        if (!data.name || data.name.length === 0) {
            errors.push("Не е въведено името на служителя.")
        }
        if (emplPPH.length === 0 || !emplPPH[emplPPH.length-1].pph) {
            errors.push("Не е въведена базова ставка за служителя.")
        }
        if (!data.supervisor_pph) {
            // errors.push("Не е въведена супервайзор ставка за служителя.")
            data.supervisor_pph = 0;
        }

        if (errors.length === 0) {
            setSaving(true);
            try {
                await assertEmployeeEmailAvailable(data.email || "", employee?.id);
                await save(data);
            } catch (error) {
                showErrorsListInToast("Грешка", [error instanceof Error ? error.message : "Неуспешно записване на служителя."]);
            } finally {
                setSaving(false);
            }
        } else {
            showErrorsListInToast("Внимание", errors);
            setSaving(false);
        }
    }

    const emplPPHLastIdx = emplPPH.length - 1;

    function addPPH() {
        setEmplPPH([
            ...emplPPH,
            createEmployeeRate(emplPPH.length, getEmployeeRateActor(currentAppUser))
        ]);
    }

    function setPPH(idx: number, pph: RREmployeePPH) {
        const newList = [...emplPPH];
        newList[idx] = prepareEmployeeRateEdit(
            emplPPH[idx],
            pph,
            idx,
            getEmployeeRateActor(currentAppUser),
        );
        setEmplPPH(newList);
    }

    return (
        <><Form className={""} onSubmit={handleSubmit}>
            <Modal.Body>
                <h5 className={"mb-3"}>
                    {employee ? "Корекция" : "Добавяне"} на служител
                </h5>

                <Row>
                    <Col>
                        <Row>
                            {/*<Col>*/}
                            {/*    <Form.Group>*/}
                            {/*        <Form.Label>Отдел:</Form.Label>*/}
                            {/*        <FormSelect defaultValue={typeof employee?.department} required name={"department"}>*/}
                            {/*            {*/}
                            {/*                department === "catering" ?*/}
                            {/*                    <option*/}
                            {/*                        value={RRDEPARTMENT_MONTAGE}>{RRDEPARTMENT_CETARING_LABEL}</option> :*/}
                            {/*                    <option*/}
                            {/*                        value={RRDEPARTMENT_MONTAGE}>{RRDEPARTMENT_MONTAGE_LABEL}</option>*/}
                            {/*            }*/}
                            {/*            <option value={RRDEPARTMENT_WAREHOUSE}>{RRDEPARTMENT_WAREHOUSE_LABEL}</option>*/}
                            {/*        </FormSelect>*/}
                            {/*    </Form.Group>*/}
                            {/*</Col>*/}
                            <Col>
                                <Row>
                                    <Col xs={2}>
                                        <Form.Group>
                                            <Form.Label>ЕГН:</Form.Label>
                                            <Form.Control type="text" name="egn" defaultValue={employee?.egn}/>
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group>
                                            <Form.Label>Име:</Form.Label>
                                            <Form.Control type="text" name="name" defaultValue={employee?.name}
                                                          required/>
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group>
                                            <Form.Label>E-mail:</Form.Label>
                                            <Form.Control type="text" name="email" defaultValue={employee?.email || ""}/>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <Form.Group>
                                    <Form.Label>Телефон:</Form.Label>
                                    <Form.Control type="text" name="phone" defaultValue={employee?.phone}/>
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group>
                                    <Form.Label>Назначен на дата:</Form.Label>
                                    <ReactDatePicker onChange={date => setStartDate(date || new Date())}
                                                     required name={"startDate"}
                                                     dateFormat="dd.MM.yyyy"
                                                     selected={startDate}
                                                     className={"form-control"}
                                                     value={dateToString(startDate)}/>
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group>
                                    <Form.Label>Освободен на дата:</Form.Label>
                                    <ReactDatePicker onChange={date => setLeaveDate(date)}
                                                     name={"leaveDate"}
                                                     isClearable
                                                     dateFormat="dd.MM.yyyy"
                                                     selected={leaveDate}
                                                     className={"form-control"}
                                                     value={dateToString(leaveDate)}/>
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className={"mt-2"}>
                            &nbsp;
                            <hr/>
                            &nbsp;
                        </div>

                        <h5 className={"mb-3"}>Ставки</h5>

                        <div style={{maxHeight: "200px", overflowY: "auto"}}>
                            <Table variant={"light"} size={"sm"} bordered hover>
                                <thead>
                                <tr>
                                    <th>От дата</th>
                                    <th>Направена на</th>
                                    <RightAlignedColumn header>Базова ставка</RightAlignedColumn>
                                    <RightAlignedColumn header>Суперв. ставка</RightAlignedColumn>
                                    <CenteredColumn header>Одобрена?</CenteredColumn>
                                </tr>
                                </thead>
                                <tbody>
                                {
                                    emplPPH.map(
                                        (pph, idx) =>
                                            <tr key={`${idx.toString()}_${getEmployeeRateEffectiveFrom(pph).toString()}`} className={
                                                pph.isApproved ? "lh-2em" : " lh-2em text-danger"
                                            }>
                                                {
                                                    idx === emplPPHLastIdx && !saving ?  <>
                                                        <td>
                                                            <ReactDatePicker
                                                                className={"w-100"}
                                                                value={dateToString(getEmployeeRateEffectiveFrom(pph)) === "01.01.1999" ? "" : dateToString(getEmployeeRateEffectiveFrom(pph))}
                                                                minDate={emplPPH[idx - 1] ? getEmployeeRateEffectiveFrom(emplPPH[idx - 1]) : new Date(1900, 0, 1)}
                                                                selected={dateToString(getEmployeeRateEffectiveFrom(pph)) === "01.01.1999" ? new Date() : getEmployeeRateEffectiveFrom(pph)}
                                                                onChange={(newDate) => {
                                                                    const effectiveFrom = newDate || new Date();
                                                                    setPPH(idx, {
                                                                        ...pph,
                                                                        fromDate: effectiveFrom,
                                                                        effectiveFrom,
                                                                    });
                                                                }}/>
                                                        </td>
                                                        <td>{dateToString(pph.createdAt) || "—"}</td>
                                                        <RightAlignedColumn>
                                                            <StringInputField label={""}
                                                                              value={zeroToEmptyStringAsCurrency(pph.pph)}
                                                                              fieldClass={"text-end"}
                                                                              onChange={newValue => {
                                                                                  setPPH(idx, {
                                                                                      ...pph,
                                                                                      pph: textToFloat(newValue) || 0
                                                                                  })
                                                                              }}/>
                                                        </RightAlignedColumn>
                                                        <RightAlignedColumn>
                                                            <StringInputField label={""}
                                                                              value={zeroToEmptyStringAsCurrency(pph.supervisor_pph)}
                                                                              fieldClass={"text-end"}
                                                                              onChange={newValue => {
                                                                                  setPPH(idx, {
                                                                                      ...pph,
                                                                                      supervisor_pph: textToFloat(newValue) || 0
                                                                                  })
                                                                              }}/>
                                                        </RightAlignedColumn>
                                                        <CenteredColumn> {pph.isApproved ? "ДА" : "НЕ"} </CenteredColumn>
                                                    </> : <>
                                                        <td> {dateToString(getEmployeeRateEffectiveFrom(pph))} </td>
                                                        <td>{dateToString(pph.createdAt) || "—"}</td>
                                                        <RightAlignedColumn> {zeroToEmptyStringAsCurrency(pph.pph)} </RightAlignedColumn>
                                                        <RightAlignedColumn> {zeroToEmptyStringAsCurrency(pph.supervisor_pph)} </RightAlignedColumn>
                                                        <CenteredColumn> {pph.isApproved ? "ДА" : "НЕ"} </CenteredColumn>
                                                    </>
                                                }
                                            </tr>
                                    )
                                }
                                </tbody>
                            </Table>
                        </div>
                        <div className={"text-end mt-2"}>
                            <AddIconButton onClick={() => addPPH()}/>
                        </div>
                    </Col>

                </Row>

            </Modal.Body>
            <Modal.Footer>
                <Row className={"w-100"}>
                    {
                        saving && <Col xs={12} className={"text-center"}>
                            <Spinner animation={"border"}/>
                        </Col>
                    }

                    {
                        !saving &&
                        <>
                            <Col xs={6}>
                                <Button variant={"outline-secondary"} onClick={() => {
                                    onDone()
                                }}>
                                    <FontAwesomeIcon icon={faArrowLeft}/> Към списъка
                                </Button>
                            </Col>
                            <Col xs={6} className={"text-end"}>
                                <Button type={"submit"}>
                                    <FontAwesomeIcon icon={faSave}/> Запиши
                                </Button>
                            </Col>
                        </>
                    }
                </Row>
            </Modal.Footer>
        </Form>
        </>
    )
}
